import React, { useState, useEffect } from 'react';
import { 
  CloudArrowUpIcon,
  DocumentIcon,
  XMarkIcon,
  TagIcon,
  CalendarIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import { useDocumentStorage } from '../../hooks/useDocumentStorage';
import { useDocumentStorageCategories } from '../../hooks/useDocumentStorageCategories';
import { useDocumentTags } from '../../hooks/useDocumentTags';
import { useCustomers } from '../../hooks/useCustomers';
import { useAuth } from '../../contexts/AuthContext';
import { DocumentStorage, DocumentStorageFormData } from '../../types/documentStorage';
import { Button } from '../common/Button';
import { Input } from '../common/Input';
import CustomerSelector from '../common/CustomerSelector';
import ConfirmDeleteModal from '../common/ConfirmDeleteModal';
import DragDropUploader from './DragDropUploader';
import toast from 'react-hot-toast';

interface DocumentStorageFormProps {
  document?: DocumentStorage | null;
  customerId?: string;
  folderId?: string;
  onClose: () => void;
}

export default function DocumentStorageForm({ document, customerId, folderId, onClose }: DocumentStorageFormProps) {
  const [formData, setFormData] = useState<DocumentStorageFormData>({
    title: '',
    description: '',
    categoryId: '',
    customerId: customerId || '',
    folderId: folderId || undefined,
    tags: [],
    isPublic: false,
    creationDate: new Date(),
    expirationDate: undefined,
    file: undefined
  });
  const [newTag, setNewTag] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { createDocument, updateDocument, removeFile } = useDocumentStorage();
  const { categories } = useDocumentStorageCategories();
  const { tags: documentTags, addTag: addDocumentTag } = useDocumentTags();
  const { customers } = useCustomers();
  const { user } = useAuth();

  // Populate form when editing
  useEffect(() => {
    if (document) {
      setFormData({
        title: document.title,
        description: document.description || '',
        categoryId: document.category?.id || '',
        customerId: document.customerId,
        folderId: document.folderId || undefined,
        tags: document.tags || [],
        isPublic: document.isPublic,
        creationDate: document.creationDate || new Date(),
        expirationDate: document.expirationDate || undefined,
        file: undefined
      });
    }
  }, [document]);

  // Update folderId when prop changes (for new documents)
  useEffect(() => {
    if (!document && folderId) {
      setFormData(prev => ({
        ...prev,
        folderId: folderId
      }));
    }
  }, [folderId, document]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error('A cím megadása kötelező');
      return;
    }
    
    if (!formData.categoryId) {
      toast.error('A kategória kiválasztása kötelező');
      return;
    }
    
    if (!formData.customerId) {
      toast.error('Az ügyfél kiválasztása kötelező');
      return;
    }

    setIsSubmitting(true);

    try {
      if (document) {
        await updateDocument(document.id, formData);
      } else {
        await createDocument(formData);
      }
      onClose();
    } catch (error) {
      console.error('Error saving document storage:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileSelect = (file: File) => {
    setFormData(prev => ({ ...prev, file }));
  };

  const handleFileDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
  };

  const addTag = async () => {
    const tagName = newTag.trim();
    if (!tagName) return;

    try {
      // Check if tag already exists by name
      const existingTag = documentTags.find((t: any) => 
        t.name.toLowerCase() === tagName.toLowerCase() || 
        (t.title && t.title.toLowerCase() === tagName.toLowerCase())
      );

      let tagId: string;
      if (existingTag) {
        tagId = existingTag.id;
      } else {
        // Create new tag and get its ID
        tagId = await addDocumentTag(tagName);
      }

      // Check if tag ID is already in the form
      if (!formData.tags?.includes(tagId)) {
        setFormData(prev => ({
          ...prev,
          tags: [...(prev.tags || []), tagId]
        }));
        setNewTag('');
      } else {
        toast.error('Ez a címke már hozzá van adva!');
      }
    } catch (error) {
      console.error('Error adding tag:', error);
      toast.error('Hiba a címke hozzáadásakor');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: (prev.tags || []).filter(tag => tag !== tagToRemove)
    }));
  };

  const handleDeleteFile = async () => {
    if (!document?.id) return;
    
    try {
      await removeFile(document.id);
      setShowDeleteModal(false);
      toast.success('Fájl sikeresen törölve');
      // Close form and refresh parent
      onClose();
    } catch (error) {
      console.error('Fájl törlési hiba:', error);
      toast.error('Fájl törlése sikertelen');
    }
  };

  const formatFileSize = (bytes: number) => {
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <>
    <form onSubmit={handleSubmit} className="space-y-6 p-6">
      {/* Organization Info */}
      {user?.organizationId && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-4">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center text-blue-800 dark:text-blue-200">
              <UserIcon className="h-4 w-4 mr-2" />
              <span className="font-medium">Szervezeti dokumentum</span>
              <span className="ml-2 text-blue-600 dark:text-blue-400 text-xs">
                (Csak a szervezet tagjai láthatják)
              </span>
            </div>
            <div className="flex items-center">
              <span className={`inline-flex items-center px-3 py-1 text-xs font-medium rounded-full ${
                formData.isPublic 
                  ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-200' 
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
              }`}>
                {formData.isPublic ? '🌐 Nyilvános' : '🔒 Privát'}
              </span>
            </div>
          </div>
        </div>
      )}
      {/* Basic Information Section */}
      <div className="crm-card">
        <div>
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--crm-border)] bg-[var(--crm-bg)] rounded-t-[10px]">
            <DocumentIcon className="w-4 h-4 text-gray-400" />
            <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">Alapinformációk</h3>
          </div>
          <div className="p-4">
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Cím *
                </label>
                <Input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Dokumentum címe"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Kategória *
                </label>
                <select
                  value={formData.categoryId}
                  onChange={(e) => setFormData(prev => ({ ...prev, categoryId: e.target.value }))}
                  className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  required
                >
                  <option value="" className="text-gray-500 dark:text-gray-400">Válasszon kategóriát</option>
                  {categories.map(category => (
                    <option key={category.id} value={category.id} className="text-gray-900 dark:text-white bg-white dark:bg-gray-800">
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Leírás
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Dokumentum leírása"
                rows={3}
                className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Customer Section */}
      <div className="crm-card">
        <div>
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--crm-border)] bg-[var(--crm-bg)] rounded-t-[10px]">
            <UserIcon className="w-4 h-4 text-gray-400" />
            <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">Ügyfél *</h3>
          </div>
          <div className="p-4">
            <div className={customerId ? 'opacity-50 pointer-events-none' : ''}>
              <CustomerSelector
                selectedCustomer={customers.find(c => c.id === formData.customerId) || null}
                onCustomerSelect={(customer: any) => setFormData(prev => ({ 
                  ...prev, 
                  customerId: customer?.id || '' 
                }))}
                placeholder="Válasszon ügyfelet"
              />
            </div>
          </div>
        </div>
      </div>

      {/* File Upload Section */}
      <div className="crm-card">
        <div>
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--crm-border)] bg-[var(--crm-bg)] rounded-t-[10px]">
            <CloudArrowUpIcon className="w-4 h-4 text-gray-400" />
            <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">Fájl feltöltése</h3>
          </div>
          <div className="p-4">
            
            {/* Existing File Display (when editing) */}
            {document && document.fileName && !formData.file && (
              <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <DocumentIcon className="h-8 w-8 text-blue-500" />
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {document.fileName}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {formatFileSize(document.fileSize || 0)} • Jelenlegi fájl
                      </div>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    {document.downloadUrl && (
                      <a
                        href={document.downloadUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 text-sm"
                      >
                        Letöltés
                      </a>
                    )}
                    <button
                      type="button"
                      onClick={() => setShowDeleteModal(true)}
                      className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 text-sm"
                    >
                      Törlés
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Modern Drag & Drop Uploader */}
            <DragDropUploader
              onFilesSelected={(files) => {
                if (files.length > 0) {
                  handleFileSelect(files[0]);
                }
              }}
              maxFiles={1}
              maxFileSize={50}
              acceptedTypes={['*']}
              disabled={isSubmitting}
            />
          </div>
        </div>
      </div>

      {/* Additional Settings Section */}
      <div className="crm-card">
        <div>
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--crm-border)] bg-[var(--crm-bg)] rounded-t-[10px]">
            <TagIcon className="w-4 h-4 text-gray-400" />
            <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">További beállítások</h3>
          </div>
          <div className="p-4">
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Creation Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Kelt dátum *
                </label>
                <div className="relative">
                  <Input
                    type="date"
                    value={formData.creationDate ? formData.creationDate.toISOString().split('T')[0] : ''}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      creationDate: e.target.value ? new Date(e.target.value) : new Date() 
                    }))}
                  />
                  <CalendarIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                </div>
              </div>

              {/* Expiration Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Lejárati dátum
                </label>
                <div className="relative">
                  <Input
                    type="date"
                    value={formData.expirationDate ? formData.expirationDate.toISOString().split('T')[0] : ''}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      expirationDate: e.target.value ? new Date(e.target.value) : undefined 
                    }))}
                  />
                  <CalendarIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4">
              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Címkék
                </label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {(formData.tags || []).map((tagId, index) => {
                    const tagObj = documentTags.find((t: any) => t.id === tagId);
                    const displayName = tagObj?.title || tagObj?.name || tagId;
                    return (
                      <span
                        key={index}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-200"
                      >
                        <TagIcon className="h-3 w-3 mr-1" />
                        {displayName}
                        <button
                          type="button"
                          onClick={() => removeTag(tagId)}
                          className="ml-1 text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
                        >
                          <XMarkIcon className="h-3 w-3" />
                        </button>
                      </span>
                    );
                  })}
                </div>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    placeholder="Új címke"
                    onKeyDown={async (e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        await addTag();
                      }
                    }}
                  />
                  <Button
                    type="button"
                    onClick={() => addTag()}
                    variant="outline"
                    size="sm"
                  >
                    Hozzáadás
                  </Button>
                </div>
              </div>
            </div>
            
            {/* Visibility */}
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.isPublic}
                  onChange={(e) => setFormData(prev => ({ ...prev, isPublic: e.target.checked }))}
                  className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  Nyilvános dokumentum (ügyfél számára látható)
                </span>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          disabled={isSubmitting}
        >
          Mégse
        </Button>
        <Button
          type="submit"
          disabled={isSubmitting}
          className="min-w-[120px]"
        >
          {isSubmitting ? 'Mentés...' : (document ? 'Frissítés' : 'Létrehozás')}
        </Button>
      </div>
    </form>

    {/* Delete File Modal */}
    <ConfirmDeleteModal
      isOpen={showDeleteModal}
      onClose={() => setShowDeleteModal(false)}
      onConfirm={handleDeleteFile}
      title="Fájl törlése"
      deleteMessage={`Biztosan törli a "${document?.fileName}" fájlt?`}
      deleteWarning="Ez a művelet nem vonható vissza."
    />
    </>
  );
}
